import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Star, MapPin, Tag, Lightbulb, Image as ImageIcon, X, Sparkles, CheckCircle, Search, ChevronDown } from "lucide-react";
// CategoryNav removed
import { usePreviewSafeData } from '../hooks/usePreviewSafeData';
import NewsTicker from './NewsTicker';

const CATEGORIES = ["All Categories", "Restaurants", "Events", "Concerts/Events", "Temples/Spiritual Services", "Shopping", "Grocery", "Movies", "Party Venues", "Event Management", "Influencers"];
const LOCATIONS = ["Addison", "Allen", "Anna", "Arlington", "Aubrey", "Carrollton", "Cedar Hill", "Coppell", "Dallas", "Denton", "DeSoto", "Duncanville", "Farmers Branch", "Flower Mound", "Fort Worth", "Frisco", "Garland", "Grand Prairie", "Hutchins", "Irving", "Lewisville", "Mansfield", "McKinney", "Mesquite", "Midlothian", "Plano", "Richardson", "Rockwall", "Rowlett", "Terrell", "Other"];

const inputClass =
  "w-full border rounded-xl px-3.5 py-2.5 text-[13px] text-gray-800 outline-none transition-all focus:ring-2 focus:ring-orange-200 focus:border-orange-400 placeholder:text-gray-400";

const AVATAR_COLORS = ["#f97316", "#2563eb", "#16a34a", "#7c3aed", "#db2777", "#0891b2"];
function initialsOf(name: string) {
  const p = name.trim().split(/\s+/);
  return ((p[0]?.[0] || "A") + (p[1]?.[0] || "")).toUpperCase();
}
function colorOf(name: string) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}
function fmtDate(iso?: string) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

// The SDK's preview-mode mock storage wraps inserted fields inside a nested
// `data` property instead of spreading them at the top level (the real
// production API returns a flat row). Without normalizing, a submitted card
// looks right for a moment (optimistic entry) then goes blank/disappears once
// it's swapped for the "server" row or pulled in via refetch — title,
// experience, submittedName, etc. no longer exist at the top level so nothing
// renders. Normalize both shapes so display never depends on which one came back.
function normalizeRow(r: any) {
  if (r && typeof r === "object" && r.data && typeof r.data === "object") {
    // Fall back to the nested `data.status` when the top-level `status` is
    // missing — an admin Approve/Reject write can land inside `data` rather
    // than the row's top level, which made approved experiences invisible
    // here even though Admin's own normalizer (which does fall back) showed
    // "Approved" correctly.
    return { ...r.data, _id: r._id, createdAt: r.createdAt || r.data.createdAt, status: r.status ?? r.data.status };
  }
  return r;
}

// Downscale + compress before turning into a data URL. The platform's API
// rejects large JSON bodies, so sending a raw multi-MB photo makes the whole
// submission fail to save. Matching the admin uploader (maxDim 1000, q0.82),
// which is proven to save on the platform, keeps the payload small.
function fileToDataUrl(file: File, maxDim = 1000, quality = 0.82): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith("image/")) {
      reject(new Error("Please choose an image file."));
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read the file."));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("That file isn't a valid image."));
      img.onload = () => {
        let { width, height } = img;
        const scale = Math.min(1, maxDim / Math.max(width, height));
        width = Math.round(width * scale);
        height = Math.round(height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          resolve(reader.result as string); // fallback: original data URL
          return;
        }
        ctx.drawImage(img, 0, 0, width, height);
        const out =
          file.type === "image/png" && scale === 1
            ? canvas.toDataURL("image/png")
            : canvas.toDataURL("image/jpeg", quality);
        resolve(out);
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  });
}

function Stars({ value, size = 16 }: { value: number; size?: number }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star key={s} style={{ width: size, height: size }} fill={value >= s ? "#f97316" : "none"} stroke={value >= s ? "#f97316" : "#d1d5db"} strokeWidth={1.5} />
      ))}
    </div>
  );
}

// Interactive star picker used in the submission form — click sets the
// rating, hover previews it.
function StarPicker({ value, onChange, size = 22 }: { value: number; onChange: (v: number) => void; size?: number }) {
  const [hover, setHover] = useState(0);
  const shown = hover || value;
  return (
    <div className="flex items-center gap-1" onMouseLeave={() => setHover(0)}>
      {[1, 2, 3, 4, 5].map((s) => (
        <button
          key={s}
          type="button"
          onClick={() => onChange(s)}
          onMouseEnter={() => setHover(s)}
          className="p-0.5 -m-0.5"
          aria-label={`Rate ${s} star${s > 1 ? "s" : ""}`}
        >
          <Star
            style={{ width: size, height: size }}
            fill={shown >= s ? "#f97316" : "none"}
            stroke={shown >= s ? "#f97316" : "#d1d5db"}
            strokeWidth={1.5}
            className="transition-colors"
          />
        </button>
      ))}
      {value > 0 && <span className="text-[12px] text-gray-500 ml-1.5 font-medium">{value}/5</span>}
    </div>
  );
}

function ExperienceCard({ exp, index }: { exp: any; index: number }) {
  const name = exp.submittedName || "Anonymous";
  const color = exp.isAnonymous ? "#94a3b8" : colorOf(name);
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: Math.min(index, 6) * 0.05 }}
      className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-[13px] font-bold shrink-0" style={{ background: color }}>
          {exp.isAnonymous ? "A" : initialsOf(name)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[14px] font-semibold text-gray-900">{name}</span>
            {(exp.createdAt || exp.submittedAt) && <span className="text-[11px] text-gray-400">{fmtDate(exp.createdAt || exp.submittedAt)}</span>}
          </div>
          <div className="mt-1"><Stars value={Number(exp.rating) || 0} /></div>
          <div className="flex flex-wrap items-center gap-1.5 mt-2">
            {exp.title && <span className="text-[10.5px] font-semibold text-green-700 bg-green-50 rounded-full px-2 py-0.5">{exp.title}</span>}
            {exp.location && (
              <span className="text-[10.5px] font-semibold text-blue-700 bg-blue-50 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {exp.location}
              </span>
            )}
          </div>
          {exp.experience && <p className="text-[13px] text-gray-600 leading-relaxed mt-2.5">{exp.experience}</p>}
          {exp.suggestions && (
            <p className="text-[12px] text-gray-500 leading-relaxed mt-2 flex items-start gap-1.5">
              <Lightbulb className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
              <span><span className="font-semibold text-gray-600">Suggestion:</span> {exp.suggestions}</span>
            </p>
          )}
          {exp.imageUrl && (
            <img src={exp.imageUrl} alt="Experience" className="mt-3 w-full max-w-[260px] h-[150px] object-cover rounded-lg border border-gray-100" />
          )}
        </div>
      </div>
    </motion.div>
  );
}

// The real backend's update endpoint only allows a row to be modified by
// the exact session that created it — confirmed directly against the live
// API ("Not authorized to modify this row" / "Valid session required for
// updates"). A submitted experience is created by the VISITOR's session;
// Admin's Approve/Reject is clicked from the ADMIN's session — so that
// update can never land in the real database, no matter what status field
// Admin appears to set locally. Moderation decisions are now stored as
// independent INSERTs (always allowed) — as marker rows inside the SAME
// `experiences` table (source: "moderation-marker"), NOT in a separate
// `experience_moderation` table. Confirmed directly against the live API:
// `experience_moderation` has public read permanently disabled ("Public
// read access is not allowed for this table"), even right after explicitly
// re-provisioning it with allowPublicRead: true — so no session, including
// this public page's, could ever read a moderation record from it.
// `experiences` itself DOES allow public reads from any session (verified
// with a fresh random session id), so mirror Admin's same marker-row
// lookup here instead.
const MODERATION_MARKER_SOURCE = "moderation-marker";
// Mirrors the same marker Admin.tsx now writes on Delete (see
// DELETION_MARKER_SOURCE there) — real DELETE always fails cross-session, so
// Delete inserts a deletion-marker row instead. Filter those out here too so
// a deleted submission actually disappears from the public page.
const DELETION_MARKER_SOURCE = "deletion-marker";

// IMPORTANT: the real backend enforces a LOCKED schema on this table — any
// field name that wasn't part of the columns established by the very first
// inserts (title, experience, location, source, status, createdAt,
// submittedName, isAnonymous, imageUrl, suggestions, submittedAt) is
// silently stripped server-side even though the insert response says
// success: true. Confirmed directly against the live API: targetExperienceId
// never persisted on any marker row (grepped raw API response — the field
// is simply absent from every stored marker), which meant this lookup was
// always comparing against `undefined` and could never match a real
// experience id. Fix: repurpose the existing `location` column to carry the
// target experience id on marker rows (markers don't need a real location).
function latestModerationStatus(markerRows: any[], experienceId: string): string | null {
  const matches = markerRows
    .filter((m: any) => m.source === MODERATION_MARKER_SOURCE && String(m.location) === String(experienceId))
    .sort((a: any, b: any) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());
  return matches.length ? matches[0].status : null;
}

function isDeleted(deletionMarkerRows: any[], experienceId: string): boolean {
  return deletionMarkerRows.some((m: any) => String(m.location) === String(experienceId));
}

export default function Experiences() {
  const navigate = useNavigate();
  const { rows, insert, refetch } = usePreviewSafeData("experiences");
  const [optimisticRows, setOptimisticRows] = useState<any[]>([]);
  const pendingIdRef = useRef<string | null>(null); // track optimistic entry to clean up after persistence

  // hero search/location/category filters
  const [query, setQuery] = useState("");
  const [communitySearch, setCommunitySearch] = useState("");
  const [location, setLocation] = useState("All Locations");
  const [categoryFilter, setCategoryFilter] = useState("All Categories");
  const [catOpen, setCatOpen] = useState(false);
  const [locOpen, setLocOpen] = useState(false);
  const catRef = useRef<HTMLDivElement>(null);
  const locRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function h(e: MouseEvent) {
      if (catRef.current && !catRef.current.contains(e.target as Node)) setCatOpen(false);
      if (locRef.current && !locRef.current.contains(e.target as Node)) setLocOpen(false);
    }
    if (catOpen || locOpen) {
      document.addEventListener("mousedown", h);
      return () => document.removeEventListener("mousedown", h);
    }
  }, [catOpen, locOpen]);

  // form state
  const [form, setForm] = useState({ title: "", location: "", experience: "", suggestions: "", name: "", rating: 0 });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageName, setImageName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [justPosted, setJustPosted] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  function update(field: string, value: string) {
    setForm((p) => ({ ...p, [field]: value }));
    if (errors[field]) setErrors((p) => { const n = { ...p }; delete n[field]; return n; });
  }

  function updateRating(value: number) {
    setForm((p) => ({ ...p, rating: value }));
    if (errors.rating) setErrors((p) => { const n = { ...p }; delete n.rating; return n; });
  }

  function handleImage(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageName(file.name);
    setImagePreview(URL.createObjectURL(file));
  }
  function removeImage() {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImagePreview(null);
    setImageName("");
    if (fileRef.current) fileRef.current.value = "";
  }

  function validate() {
    const e: Record<string, string> = {};
    if (!form.title.trim()) e.title = "Enter a title.";
    if (!form.experience.trim()) e.experience = "Share your experience.";
    if (!form.rating) e.rating = "Pick a star rating.";
    return e;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setSubmitError(null);
    setSubmitting(true);

    // Snapshot ALL values synchronously BEFORE any async gap
    const snapTitle = form.title.trim();
    const snapLocation = form.location;
    const snapExperience = form.experience.trim();
    const snapSuggestions = form.suggestions.trim() || null;
    const snapRating = form.rating;
    const snapName = form.name.trim();
    const snapSubmittedName = snapName || "Anonymous";
    const snapIsAnonymous = !snapName;
    const snapCreatedAt = new Date().toISOString();
    const imageFile = fileRef.current?.files?.[0] ?? null;

    try {
      const imageUrl = imageFile ? await fileToDataUrl(imageFile) : undefined;

      const localId = `local-${Date.now()}`;

      // Build raw record — then strip null/undefined so the DB doesn't reject
      const raw: Record<string, any> = {
        title: snapTitle,
        location: snapLocation || undefined,
        experience: snapExperience,
        suggestions: snapSuggestions || undefined,
        rating: snapRating || undefined,
        submittedName: snapSubmittedName,
        isAnonymous: snapIsAnonymous,
        imageUrl,
        source: "experience-form",
        status: "pending",
        submittedAt: snapCreatedAt,
        createdAt: snapCreatedAt,
      };
      const persistEntry: Record<string, any> = {};
      for (const [k, v] of Object.entries(raw)) {
        if (v !== undefined && v !== null) persistEntry[k] = v;
      }

      // Optimistic entry shows full data (nulls are fine for UI rendering)
      const optimisticEntry = { _id: localId, ...raw, imageUrl: imageUrl || null, suggestions: snapSuggestions || null };

      pendingIdRef.current = localId;
      setOptimisticRows((prev) => [optimisticEntry, ...prev]);

      // reset form right away so user sees the card instantly
      setForm({ title: "", location: "", experience: "", suggestions: "", name: "", rating: 0 });
      removeImage();
      setJustPosted(true);
      setTimeout(() => setJustPosted(false), 4000);

      // Persist (no _id — DB generates it). Capture the server row.
      const serverRowRaw = await insert(persistEntry);
      const serverRow = normalizeRow(serverRowRaw) as any;

      // Swap optimistic entry for the real server row (has real _id from DB)
      setOptimisticRows((prev) =>
        prev.map((r) => (r._id === localId ? { ...serverRow, imageUrl: serverRow.imageUrl || null, suggestions: serverRow.suggestions || null } : r))
      );

      // Refetch to pull in any other concurrent submissions
      await refetch();

      // Clean up: if the server row is now in `rows`, ditch the optimistic copy
      setOptimisticRows((prev) => prev.filter((r) => r._id !== localId));
      pendingIdRef.current = null;
    } catch (err) {
      console.error("Experiences insert failed:", err);
      if (pendingIdRef.current) {
        setOptimisticRows((prev) => prev.filter((r) => r._id !== pendingIdRef.current!));
        pendingIdRef.current = null;
      }
      setSubmitError(err instanceof Error ? err.message : "Couldn't reach the server. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  // Merge optimistic rows with server rows (deduplicate by _id).
  // Normalize server rows first — preview-mode mock storage nests fields
  // under `.data`, and without this every fetched/refetched row would render
  // blank (see normalizeRow above), making posted experiences vanish.
  const normalizedRows = (rows || []).map(normalizeRow);
  const serverIds = new Set(normalizedRows.map((r: any) => r._id));
  const dedupedOptimistic = optimisticRows.filter((r) => !serverIds.has(r._id));
  const allRows = [...dedupedOptimistic, ...normalizedRows];

  // Moderation + deletion decisions are stored as marker rows inside this
  // SAME `experiences` table (see comments above MODERATION_MARKER_SOURCE /
  // DELETION_MARKER_SOURCE) — pull them out of the rows we already fetched
  // instead of a separate table.
  const markerRows = normalizedRows.filter((r: any) => r.source === MODERATION_MARKER_SOURCE);
  const deletionMarkerRows = normalizedRows.filter((r: any) => r.source === DELETION_MARKER_SOURCE);

  // newest first + apply hero search/location filters + community search
  const q = query.trim().toLowerCase();
  const cs = communitySearch.trim().toLowerCase();
  const experiences = [...allRows]
    .sort((a, b) => new Date(b.createdAt || b.submittedAt || 0).getTime() - new Date(a.createdAt || a.submittedAt || 0).getTime())
    .filter((r) => {
      const matchesQ = !q || [r.experience, r.submittedName, r.title, r.location, r.suggestions].some((v) => (v || "").toLowerCase().includes(q));
      const matchesCS = !cs || [r.experience, r.submittedName, r.title, r.location, r.suggestions].some((v) => (v || "").toLowerCase().includes(cs));
      const matchesLoc = location === "All Locations" || r.location === location;
      const matchesCat = categoryFilter === "All Categories" || r.category === categoryFilter;
      // Marker/probe rows (moderation-marker, deletion-marker, and any leftover
      // __schema_probe__-style debug inserts) live in this SAME `experiences`
      // table and were rendering as fake "Anonymous" cards because nothing
      // excluded them from the public list. Real visitor submissions always
      // carry source "experience-form" — require that explicitly rather than
      // blocklisting every internal tag by name.
      const isInternalRow =
        r.source !== "experience-form" ||
        /^__.*__$/.test(String(r.title || "").trim()) ||
        /^__.*__$/.test(String(r.experience || "").trim());
      // Public list only shows admin-approved experiences. New submissions default
      // to status "pending" (see handleSubmit) and stay hidden here until an admin
      // approves them from Admin > Experiences. The submitter's own just-submitted
      // card stays visible to them right after posting (still tracked in
      // optimisticRows) so the form doesn't feel like it silently failed.
      const isOwnJustSubmitted = optimisticRows.some((o) => o._id === r._id);
      // The row's own `status` field can only ever be "pending" for
      // cross-session submissions (see comment above `latestModerationStatus`
      // — the backend rejects updates from a session that didn't create the
      // row, so Admin's approval never lands on the row itself). The real
      // decision lives in `experience_moderation`, keyed by experienceId —
      // check that FIRST and fall back to the row's own status only when no
      // moderation record exists yet.
      const modStatus = latestModerationStatus(markerRows || [], r._id);
      const isApproved = modStatus ? modStatus === "approved" : r.status === "approved";
      const deleted = isDeleted(deletionMarkerRows || [], r._id);
      return matchesQ && matchesCS && matchesLoc && matchesCat && !isInternalRow && !deleted && (isApproved || isOwnJustSubmitted);
    });

  return (
    <div className="min-h-screen bg-white">
      {/* Custom Hero with Category Dropdown */}
      <section className="relative w-full" style={{ minHeight: "160px" }}>
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="https://storage.googleapis.com/happyspace-generated-images/generated/2026-06-21/38c0a4b3b01c.jpeg"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/45" />
          <svg viewBox="0 0 1440 24" preserveAspectRatio="none" style={{ position: "absolute", left: 0, bottom: "-1px", width: "100%", height: "18px", display: "block" }}>
            <path d="M0,0 Q720,24 1440,0 L1440,24 L0,24 Z" fill="#ffffff" />
          </svg>
        </div>
        <div className="relative max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 flex items-center justify-center" style={{ zIndex: 10, minHeight: "160px" }}>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, delay: 0.1 }} className="w-full max-w-[740px]">
            <div className="rounded-md p-1.5 sm:p-0" style={{ boxShadow: "0 0 40px 12px rgba(255,255,255,0.25), 0 0 80px 24px rgba(255,255,255,0.1)" }}>
              <div className="flex flex-col gap-1.5 sm:gap-0 sm:flex-row bg-transparent sm:bg-white rounded-md sm:shadow-md h-auto sm:h-[40px]">
                <div className="flex-1 px-3 py-2.5 sm:py-0 flex items-center bg-white rounded-md sm:rounded-none sm:border-r border-gray-200 min-h-[44px] sm:min-h-[40px]">
                  <Search className="w-4 h-4 sm:w-3.5 sm:h-3.5 text-gray-400 mr-2 shrink-0" />
                  <input type="text" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search experiences…" className="w-full text-[13px] sm:text-[12px] text-gray-700 placeholder:text-gray-400 outline-none bg-transparent" />
                </div>
                {/* Category Dropdown */}
                <div ref={catRef} className="relative bg-white rounded-md sm:rounded-none sm:border-r border-gray-200 min-h-[44px] sm:min-h-[40px] sm:w-[150px] shrink-0" style={{ zIndex: 99999 }}>
                  <button type="button" onClick={() => { setCatOpen(!catOpen); setLocOpen(false); }} className="w-full h-full px-3 py-2.5 sm:py-0 flex items-center gap-1.5 text-left min-h-[44px] sm:min-h-[40px]">
                    <Tag className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                    <span className="text-[12px] text-gray-700 font-medium truncate flex-1">{categoryFilter}</span>
                    <ChevronDown className={`w-3 h-3 text-gray-400 shrink-0 transition-transform ${catOpen ? "rotate-180" : ""}`} />
                  </button>
                  {catOpen && (
                    <div className="absolute top-full left-0 bg-white border border-gray-100 rounded-lg py-1 overflow-y-auto" style={{ zIndex: 99999, marginTop: "4px", width: "190px", maxHeight: "230px", boxShadow: "0 10px 24px rgba(15,23,42,0.16)", fontFamily: "'Poppins', sans-serif" }}>
                      {CATEGORIES.map((c) => (
                        <button key={c} type="button" onClick={() => { setCategoryFilter(c); setCatOpen(false); }} className="w-full text-left flex items-center transition-colors duration-150" style={{ padding: "8px 12px", fontSize: "13px", fontWeight: categoryFilter === c ? 700 : 500, color: categoryFilter === c ? "#f97316" : "#111827", background: categoryFilter === c ? "#fff3e8" : "transparent", height: "36px" }} onMouseEnter={(e) => { if (categoryFilter !== c) (e.currentTarget as HTMLButtonElement).style.background = "#fff7ed"; }} onMouseLeave={(e) => { if (categoryFilter !== c) (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}>
                          {c}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {/* Location Dropdown */}
                <div ref={locRef} className="relative bg-white rounded-md sm:rounded-none sm:border-r border-gray-200 min-h-[44px] sm:min-h-[40px] sm:w-[150px] shrink-0" style={{ zIndex: 99999 }}>
                  <button type="button" onClick={() => { setLocOpen(!locOpen); setCatOpen(false); }} className="w-full h-full px-3 py-2.5 sm:py-0 flex items-center gap-1.5 text-left min-h-[44px] sm:min-h-[40px]">
                    <MapPin className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                    <span className="text-[12px] text-gray-700 font-medium truncate flex-1">{location}</span>
                    <ChevronDown className={`w-3 h-3 text-gray-400 shrink-0 transition-transform ${locOpen ? "rotate-180" : ""}`} />
                  </button>
                  {locOpen && (
                    <div className="absolute top-full left-0 bg-white border border-gray-100 rounded-lg py-1 overflow-y-auto" style={{ zIndex: 99999, marginTop: "4px", width: "190px", maxHeight: "230px", boxShadow: "0 10px 24px rgba(15,23,42,0.16)", fontFamily: "'Poppins', sans-serif" }}>
                      {["All Locations", ...LOCATIONS].map((l) => (
                        <button key={l} type="button" onClick={() => { setLocation(l); setLocOpen(false); }} className="w-full text-left flex items-center transition-colors duration-150" style={{ padding: "8px 12px", fontSize: "13px", fontWeight: location === l ? 700 : 500, color: location === l ? "#f97316" : "#111827", background: location === l ? "#fff3e8" : "transparent", height: "36px" }} onMouseEnter={(e) => { if (location !== l) (e.currentTarget as HTMLButtonElement).style.background = "#fff7ed"; }} onMouseLeave={(e) => { if (location !== l) (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}>
                          {l}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button type="button" onClick={() => { setCatOpen(false); setLocOpen(false); }} className="bg-[#51ba4f] hover:bg-[#43a041] text-white text-[13px] font-bold px-5 py-2.5 sm:py-0 transition-colors duration-200 shrink-0 min-h-[44px] sm:min-h-[40px] rounded-md sm:rounded-l-none w-full sm:w-auto">
                  <span className="flex items-center justify-center gap-1.5">
                    <Search className="w-3.5 h-3.5 sm:hidden" /> Search
                  </span>
                </button>
              </div>
            </div>

          </motion.div>
        </div>
      </section>
      <NewsTicker />

      <section className="bg-gray-50/50 py-8" style={{ fontFamily: "'Poppins', sans-serif" }}>
        <div className="max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 flex flex-col lg:flex-row gap-6">
          {/* LEFT: experiences list */}
          <div className="flex-1 min-w-0 order-2 lg:order-1">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-[18px] md:text-[20px] font-extrabold text-gray-900">Community Experiences</h2>
              <span className="text-[12px] text-gray-400">{experiences.length}</span>
            </div>

            {/* Community search bar */}
            <div className="mb-4">
              <div className="relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="text"
                  value={communitySearch}
                  onChange={(e) => setCommunitySearch(e.target.value)}
                  placeholder="Filter experiences by keyword…"
                  className="w-full border border-gray-200 rounded-xl pl-10 pr-4 py-2.5 text-[13px] text-gray-800 outline-none transition-all focus:ring-2 focus:ring-orange-200 focus:border-orange-400 placeholder:text-gray-400 bg-white"
                />
                {communitySearch && (
                  <button
                    onClick={() => setCommunitySearch("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>

            {experiences.length > 0 ? (
              <div className="space-y-4">
                {experiences.map((exp, i) => <ExperienceCard key={exp._id || i} exp={exp} index={i} />)}
              </div>
            ) : (
              <div className="bg-white border border-dashed border-gray-200 rounded-2xl p-10 text-center">
                <Sparkles className="w-7 h-7 text-orange-400 mx-auto mb-2" />
                <p className="text-[14px] font-semibold text-gray-700">No experiences yet</p>
                <p className="text-[12.5px] text-gray-500 mt-1">Be the first to share — use the form on the right.</p>
              </div>
            )}
          </div>

          {/* RIGHT: form */}
          <aside className="w-full lg:w-[360px] shrink-0 lg:sticky lg:top-6 self-start order-1 lg:order-2">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-5 pt-5 pb-4 border-b border-gray-100">
                <div className="inline-flex items-center gap-1.5 bg-orange-50 text-orange-600 text-[11px] font-semibold px-2.5 py-1 rounded-full mb-2">
                  <Sparkles className="w-3.5 h-3.5" /> Share an Experience
                </div>
                <p className="text-[12.5px] text-gray-500">Tell the DFW desi community about a place or service.</p>
              </div>

              <AnimatePresence>
                {justPosted && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                    <div className="mx-5 mt-4 flex items-start gap-2 bg-green-50 border border-green-200 rounded-xl px-3.5 py-2.5">
                      <CheckCircle className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                      <p className="text-[12px] text-green-800">Thanks! Your experience was submitted for review — you can preview it on the left. It'll go live once our team approves it.</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <form onSubmit={handleSubmit} noValidate className="p-5 space-y-4">
                {/* Title */}
                <div>
                  <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Title <span className="text-red-400">*</span></label>
                  <input type="text" value={form.title} onChange={(e) => update("title", e.target.value)} placeholder="Give your experience a title" className={`${inputClass} ${errors.title ? "border-red-400" : "border-gray-200"}`} />
                  {errors.title && <p className="text-red-500 text-[11px] mt-1">{errors.title}</p>}
                </div>

                {/* Rating */}
                <div>
                  <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Your Rating <span className="text-red-400">*</span></label>
                  <StarPicker value={form.rating} onChange={updateRating} />
                  {errors.rating && <p className="text-red-500 text-[11px] mt-1">{errors.rating}</p>}
                </div>

                {/* Location */}
                <div>
                  <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Location</label>
                  <div className="relative">
                    <MapPin className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <select value={form.location} onChange={(e) => update("location", e.target.value)} className={`${inputClass} pl-9 appearance-none bg-white border-gray-200 ${form.location ? "text-gray-800" : "text-gray-400"}`}>
                      <option value="">Select a location (optional)</option>
                      {LOCATIONS.map((l) => <option key={l} value={l}>{l}</option>)}
                    </select>
                  </div>
                </div>

                {/* Experience */}
                <div>
                  <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Your Experience <span className="text-red-400">*</span></label>
                  <textarea value={form.experience} onChange={(e) => update("experience", e.target.value)} rows={4} placeholder="What stood out — food, service, value, atmosphere…" className={`${inputClass} resize-none ${errors.experience ? "border-red-400" : "border-gray-200"}`} />
                  {errors.experience && <p className="text-red-500 text-[11px] mt-1">{errors.experience}</p>}
                </div>

                {/* Suggestions */}
                <div>
                  <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Suggestions</label>
                  <textarea value={form.suggestions} onChange={(e) => update("suggestions", e.target.value)} rows={2} placeholder="Anything they could improve? (optional)" className={`${inputClass} resize-none border-gray-200`} />
                </div>

                {/* Name (optional → Anonymous) */}
                <div>
                  <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Your Name</label>
                  <input type="text" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Leave blank to post as Anonymous" className={`${inputClass} border-gray-200`} />
                  <p className="text-[11px] text-gray-400 mt-1">If left empty, your review is posted as "Anonymous".</p>
                </div>

                {/* Photo */}
                <div>
                  <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Photo (optional)</label>
                  <input ref={fileRef} type="file" accept="image/*" onChange={handleImage} className="hidden" />
                  {imagePreview ? (
                    <div className="flex items-center gap-3">
                      <img src={imagePreview} alt="Preview" className="w-20 h-14 object-cover rounded-lg border border-gray-200" />
                      <button type="button" onClick={removeImage} className="inline-flex items-center gap-1 text-[12px] text-red-500 hover:text-red-600 font-medium">
                        <X className="w-3.5 h-3.5" /> Remove
                      </button>
                    </div>
                  ) : (
                    <button type="button" onClick={() => fileRef.current?.click()} className="w-full border-2 border-dashed border-gray-200 hover:border-orange-300 hover:bg-orange-50/40 rounded-xl py-4 flex flex-col items-center justify-center transition-colors">
                      <ImageIcon className="w-5 h-5 text-gray-400 mb-1" />
                      <span className="text-[12px] font-semibold text-gray-600">Add a photo</span>
                    </button>
                  )}
                </div>

                {submitError && <div className="bg-red-50 border border-red-200 text-red-700 text-[12px] rounded-xl px-3.5 py-2.5">{submitError}</div>}

                <motion.button type="submit" disabled={submitting} whileHover={submitting ? undefined : { scale: 1.02 }} whileTap={submitting ? undefined : { scale: 0.98 }} className="w-full bg-[#51ba4f] hover:bg-[#43a041] disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold text-[13.5px] py-3 rounded-xl transition-colors">
                  {submitting ? "Posting…" : "Post Experience"}
                </motion.button>
              </form>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
